import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import RestaurantList from "./components/RestaurantList";
import Filters from "./components/Filters";
import { useFilterContext } from "@/ui/head-bar/filter-context";
import CollectionMap from "../browse/components/collection-map";
import { useLocation } from "react-router-dom";
import { useThrottleFn, useDebounceFn } from "ahooks";
import { observer } from "mobx-react-lite";
import { useStores } from "@/store";
import "./index.less";
import { Footer } from "@/components";
import { Affix } from "antd";
import moment from "moment";
import RestaurantCard from "./components/RestaurantList/card";
import cn from "classnames";

const useMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { run } = useThrottleFn(
    () => {
      setIsMobile(window.innerWidth <= 768);
    },
    { wait: 500 }
  );

  useEffect(() => {
    window.addEventListener("resize", run);
    return () => {
      window.removeEventListener("resize", run);
    };
  }, [run]);

  return isMobile;
};
const extendDate = (dateStr) => {
  if (!dateStr) return null;
  const date = moment(dateStr);
  if (!date.isValid()) return null;
  return [
    date.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    date.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  ];
};
const paramsBuilder = (params) => {
  const nextParams = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      if (value.length > 0) nextParams[key] = value;
      return;
    }
    if (value === "" || value == null) return;
    nextParams[key] = value;
  });
  return nextParams;
};

const useResults = () => {
  const location = useLocation();
  const { BrowseStore } = useStores();
  const { searchState, state, pos, mapRange, order_by = 'default' } = useFilterContext();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isEmpty, setIsEmpty] = useState(false);
  const isFirstLoadRef = useRef(true);

  // 从 URL 中直接解析 region 参数作为回退值
  const urlSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params;
  }, [location.search]);

  // 获取数据的核心方法
  const fetchData = useCallback(
    (targetPage, isLoadMore = false) => {
      // 使用 URL region 作为 region 来源
      if (!urlSearch.get("region")) return Promise.resolve();

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setResults([]);
      }

      return BrowseStore.getBrowseRestaurants(
        paramsBuilder({
          ...state,
          ...searchState,
          region: urlSearch.get("region"),
          ...pos,
          ...mapRange,
          order_by,
          date: undefined,
          datetime: extendDate(urlSearch.get("date")),
          page: targetPage,
          per_page: 50,
        })
      ).then((res) => {
        const totalCount = Number(
          res?.headers?.["total_count"] || res?.headers?.["x-total-count"] || 0
        );
        const totalPagesCount = Number(res?.headers?.["total_pages"] || 1);

        setTotal(totalCount);
        setTotalPages(totalPagesCount);
        setIsEmpty(targetPage === 1 && (!res || res.length === 0));

        if (isLoadMore) {
          setResults((prev) => [...prev, ...(res || [])]);
          setLoadingMore(false);
        } else {
          setResults(res || []);
          setTimeout(() => {
            setLoading(false);
          }, 150);
        }
      });
    },
    [BrowseStore, state, searchState, pos, mapRange, urlSearch, order_by]
  );

  const { run: debouncedFetch } = useDebounceFn(
    (targetPage, isLoadMore) => {
      // 首次加载时立即执行，后续加载使用防抖
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        return fetchData(targetPage, isLoadMore);
      }
      return fetchData(targetPage, isLoadMore);
    },
    { wait: 300 }
  );

  // 当筛选条件、位置、路由变化时，从第1页重新加载
  useEffect(() => {
    const effectiveRegion = urlSearch.get("region");
    if (!effectiveRegion) return;

    setPage(1);
    debouncedFetch(1, false);
    // 滚动到顶部
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, location, pos, mapRange, order_by]);

  // 加载更多
  const loadMore = useCallback(() => {
    if (loadingMore || loading) return;
    if (page >= totalPages) return;

    const nextPage = page + 1;
    setPage(nextPage);
    debouncedFetch(nextPage, true);
  }, [loadingMore, loading, page, totalPages, debouncedFetch]);

  // 监听滚动到底部
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // 距离底部100px时触发加载
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMore]);

  return {
    loading,
    loadingMore,
    results,
    total,
    isEmpty,
    loadMore,
  };
};

const useMapResults = () => {
  const location = useLocation();
  const { BrowseStore } = useStores();
  const { searchState, state, pos } = useFilterContext();
  const [results, setResults] = useState([]);
  const isFirstLoadRef = useRef(true);

  // 从 URL 中直接解析 region 参数作为回退值
  const urlSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params;
  }, [location.search]);

  // 获取数据的核心方法
  const fetchData = useCallback(() => {
    // 使用 URL region 作为 region 来源
    if (!urlSearch.get("region")) return Promise.resolve();

    setResults([]);

    return BrowseStore.getBrowseMapRestaurants(
      paramsBuilder({
        ...state,
        ...searchState,
        region: urlSearch.get("region"),
        ...pos,
        date: undefined,
        datetime: extendDate(urlSearch.get("date")),
        page: 1,
        per_page: 2000,
      })
    ).then((res) => {
      setResults(res || []);
    });
  }, [BrowseStore, state, searchState, pos, urlSearch]);

  const { run: debouncedFetch } = useDebounceFn(
    () => {
      // 首次加载时立即执行，后续加载使用防抖
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        return fetchData();
      }
      return fetchData();
    },
    { wait: 300 }
  );

  // 当筛选条件、位置、路由变化时，从第1页重新加载
  useEffect(() => {
    const effectiveRegion = urlSearch.get("region");
    if (!effectiveRegion) return;

    debouncedFetch();
    // 滚动到顶部
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, location, pos]);

  return {
    mapResults: results,
  };
};

const useHoverId = (results) => {
  const isMobile = useMobile();
  const [hoverId, setHoverId] = useState(null);
  const { run } = useDebounceFn(
    (e) => {
      const card = e.target.closest(".explore-restaurant-card");
      if (card) {
        const id = card.getAttribute("data-id");
        setHoverId(id);
      } else {
        setHoverId(null);
      }
    },
    { wait: 500 }
  );
  useEffect(() => {
    let body = document.body;
    body.addEventListener("mouseover", run);
    return () => {
      body.removeEventListener("mouseover", run);
    };
  }, [isMobile]);

  // 当结果集变化时，重置 hoverId
  useEffect(() => {
    setHoverId(null);
  }, [results]);

  return hoverId;
};

function ExplorePage() {
  const location = useLocation();
  const isMobile = useMobile();
  const { exploreMapMode, setExploreMapMode, pageCity } = useFilterContext();
  const [headBarHeight, setHeadBarHeight] = useState(0);
  const [mapSelectedRestaurants, setMapSelectedRestaurants] = useState([]);
  const { BrowseStore } = useStores();
  const [markerIndex, setMarkerIndex] = useState([]);
  const urlQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params;
  }, [location.search]);
  // 用于追踪用户手动操作地图的版本号
  // 桌面端使用 exploreMapMode 作为全屏状态
  const isMapFullscreen = !isMobile && exploreMapMode;

  const { loading, loadingMore, results, total, isEmpty } = useResults();
  const { mapResults } = useMapResults();

  // const hoverId = useHoverId(results);

  useEffect(() => {
    const getHeadBarElement = () => document.querySelector(".ui-head-bar");

    let rafId = null;
    const measure = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const headBarElement = getHeadBarElement();
        if (!headBarElement) {
          setHeadBarHeight(30);
          return;
        }
        const next =
          Math.ceil(headBarElement.getBoundingClientRect().height) -
          (window.innerWidth <= 768 ? 130 : 0);
        setHeadBarHeight((prev) => (prev === next ? prev : next));
      });
    };

    measure();

    const headBarElement = getHeadBarElement();
    const resizeObserver =
      headBarElement && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;

    if (resizeObserver && headBarElement)
      resizeObserver.observe(headBarElement);
    window.addEventListener("resize", measure, { passive: true });

    return () => {
      window.removeEventListener("resize", measure);
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // 处理全屏模式下的body滚动
  useEffect(() => {
    if (isMapFullscreen) {
      // 禁用body滚动
      document.body.style.overflow = "hidden";
    } else {
      // 恢复body滚动
      document.body.style.overflow = "auto";
    }

    return () => {
      // 清理：组件卸载时恢复body滚动
      document.body.style.overflow = "auto";
    };
  }, [isMapFullscreen]);

  useEffect(() => {
    setMapSelectedRestaurants([]);
  }, [mapResults]);

  useEffect(() => {
    let ids = markerIndex.map((mIndex) => mapResults[mIndex].id);
    if (ids.length) {
      BrowseStore.getBrowseRestaurants({
        extraParams: urlQuery.get("lang"),
        region: urlQuery.get("region"),
        "api-key": "cgecegcegcc",
        ids: ids.join(","),
        per_page: ids.length,
      }).then(setMapSelectedRestaurants);
    } else {
      setMapSelectedRestaurants([]);
    }
  }, [markerIndex]);

  // 桌面端切换全屏状态的回调（不触发请求）
  const handleFullscreenChange = useCallback(
    (fullscreen) => {
      if (!isMobile) {
        setExploreMapMode(fullscreen);
      }
    },
    [isMobile, setExploreMapMode]
  );

  function tapMarkersHandler(indexs) {
    setMarkerIndex(indexs);
  }

  return (
    <>
      <div
        className={`expore-page ${
          isMapFullscreen ? "expore-page-fullscreen" : ""
        }`}
        style={{ "--explore-headbar-height": `${headBarHeight}px` }}
      >
        {(isMobile ? !exploreMapMode : true) && (
          <div className="expore-page-list">
            {!isMobile && (
              <Affix offsetTop={headBarHeight}>
                <Filters />
              </Affix>
            )}
            <RestaurantList
              loading={loading}
              loadingMore={loadingMore}
              results={results}
              total={total}
              isEmpty={isEmpty}
            />
          </div>
        )}

        {(isMobile ? exploreMapMode : true) && (
          <div className="expore-page-map">
            <CollectionMap
              restaurants={mapResults}
              region={pageCity}
              tapMarkers={(indexs) => tapMarkersHandler(indexs)}
            />
            {mapSelectedRestaurants.length ? (
              <div
                className={cn("map-restaurant", {
                  only: mapSelectedRestaurants.length === 1,
                })}
              >
                {mapSelectedRestaurants.map((res) => (
                  <RestaurantCard
                    restaurant={res}
                    key={res.id}
                    region={pageCity}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
      {(isMobile ? !exploreMapMode : true) && <Footer isFixed={true} />}
    </>
  );
}

export default observer(ExplorePage);
